import { drawScene, type SceneFrame } from '../render';

/** Everything about a frame except the divider position. */
export type SceneBase = Omit<SceneFrame, 'sliderX'>;

export const EXPORT_FPS = 30;
/** JPEG intermediates: fast to produce on a phone, fast for ffmpeg to decode. */
export const JPEG_QUALITY = 0.92;

/** Number of frames needed to cover `durationMs` at `fps`, including the final instant. */
export function frameCount(durationMs: number, fps: number): number {
  return Math.max(2, Math.floor(durationMs / (1000 / fps)) + 1);
}

export interface FrameRenderer {
  /** Paint the scene at `sliderX` and return it as JPEG bytes. */
  renderJpeg(sliderX: number): Promise<Uint8Array>;
  dispose(): void;
}

/**
 * Offscreen renderer that reuses `drawScene`, so exported frames are
 * pixel-identical to the live preview at the same divider position.
 */
export function createFrameRenderer(width: number, height: number, scene: SceneBase): FrameRenderer {
  if (width % 2 || height % 2) {
    // yuv420p needs even dimensions; every preset in ASPECTS is even.
    throw new Error(`Export size must be even, got ${width}x${height}`);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable for export');

  return {
    async renderJpeg(sliderX) {
      drawScene(ctx, { ...scene, sliderX });
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
      if (!blob) throw new Error('Frame encode failed (canvas.toBlob returned null)');
      return new Uint8Array(await blob.arrayBuffer());
    },
    dispose() {
      canvas.width = 0;
      canvas.height = 0;
    },
  };
}
